import { findOnlyOrThrow } from "@dhau/lang-extras";
import {
	rgbHexStringToRgbNumber,
	rgbToRgba,
} from "./utils/colour-conversions.ts";
import type { DetailFilter, OptionsList } from "./model/index.ts";
import {
	baseGetEnhanceDetailOptions,
	hexStringToBrickColour,
} from "./model/index.ts";
import type { Palette } from "./model/picture.ts";

const basePlateHexColour = "#ffffff";

const redMask24 = 0xff0000;
const greenMask24 = 0x00ff00;
const blueMask24 = 0x0000ff;

const basePlateRgb = rgbHexStringToRgbNumber(basePlateHexColour);

const pixAPicKit = {
	basePlateSize: 24,
	numberOfBasePlates: 4,
	basePlateColour: {
		rgba: rgbToRgba(basePlateRgb, 0xff),
		r: (basePlateRgb & redMask24) >> 16,
		g: (basePlateRgb & greenMask24) >> 8,
		b: basePlateRgb & blueMask24,
	},
	bricks: [
		{
			brick: hexStringToBrickColour(
				"#161616",
				-1,
				`<svg viewBox="0 0 100 115.47" xmlns="http://www.w3.org/2000/svg">
				<polygon points="50,0 100,28.87 100,86.6 50,115.47 0,86.6 0,28.87" fill="#0b0806"/>
			</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#F4F4F4",
				-2,
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 180">
			<path d="M100 180
				C40 140 0 100 0 55
				A50 50 0 0 1 100 50
				A50 50 0 0 1 200 55
				C200 100 160 140 100 180Z"
				fill="#FFF5EE" stroke="#000000" stroke-width="1"/>
		</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#D0CEC9",
				-3,
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
			<circle cx="50" cy="50" r="50" fill="lightgray"/>
		</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#8C8A88",
				-4,
				`<svg viewBox="0 0 100 95.11" xmlns="http://www.w3.org/2000/svg">
			<polygon 
				points="50,0 61.8,32.36 96.2,34.55 69,57.64 78.5,90.11 50,72.5 21.5,90.11 31,57.64 3.8,34.55 38.2,32.36" 
				fill="#72726f"/>
		</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#646464",
				-5,
				`<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
			<rect x="0" y="0" width="100" height="100" rx="15" ry="15" fill="#4c4d4f"/>
		</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#333333",
				-6,
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
			<polygon points="50,0 100,100 0,100" fill="black"/>
		</svg>`,
			),
			amount: 700,
		},
		{
			brick: hexStringToBrickColour(
				"#B2B4B2",
				-7,
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
			<polygon points="50,0 100,50 50,100 0,50" fill="gray"/>
		</svg>`,
			),
			amount: 700,
		},
	],
};

function createPixAPicPalettes(systemPalette: Palette, numberOfKits: number) {
	const kitsPalette = pixAPicKit.bricks.map(({ brick, amount }) => {
		const paletteBrick = findOnlyOrThrow(
			systemPalette,
			(p) => p.brick.rgba === brick.rgba,
			`Palette colour ${brick.identifier}: ${brick.hexString} not found`,
		).brick;
		return {
			brick: paletteBrick,
			limit: amount * numberOfKits,
		};
	});
	const nonKitsPalette = systemPalette.filter(
		(b) => !kitsPalette.some((k) => k.brick.rgba === b.brick.rgba),
	);
	return {
		kitsPalette,
		kitPlusExtrasPalette: [...kitsPalette, ...nonKitsPalette],
	};
}

const pixAPicBaseEnhanceDetailOptions: OptionsList<DetailFilter | undefined> = [
	{ value: undefined, label: "None" },
	{
		value: {
			sigma: 60,
			radiusRatio: 0.025,
		},
		label: "Low",
	},
	{
		value: {
			sigma: 100,
			radiusRatio: 0.0444,
		},
		label: "Medium",
	},
	{
		value: {
			sigma: 280,
			radiusRatio: 0.15,
		},
		label: "High",
	},
];

const defaultPixAPicDetailFilter =
	pixAPicBaseEnhanceDetailOptions[pixAPicBaseEnhanceDetailOptions.length - 1]
		.value;

function getPixAPicEnhanceDetailOptions(current: DetailFilter | undefined) {
	return baseGetEnhanceDetailOptions(pixAPicBaseEnhanceDetailOptions, current);
}

export {
	defaultPixAPicDetailFilter,
	getPixAPicEnhanceDetailOptions,
	pixAPicKit,
	createPixAPicPalettes,
};
